import React, { useMemo } from "react"

import { COMMON_COVERAGE_HISTORY_CONFIG } from "@common-shared/config/coverageHistory.config"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { TooltipElement } from "@front-shared/elements/tooltip.element"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { TOOLTIPS_CONFIG } from "../../../config/tooltips.config"
import { ButtonDropdownElement } from "../../../elements/buttonDropdown.element"
import { AuthHelper } from "../../../helpers/auth.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { profileSelector } from "../../../store/selectors/profile.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { policiesExportList } from "../../../store/thunks/policies.thunks"
import { policiesExportListToEmail } from "../../../store/thunks/policiesExport.thunks"

import exportIcon from "@front-shared/assets/images/icons/download-up-dark.svg"

export const PoliciesExportListComponent: React.FunctionComponent<
    IPoliciesExportListComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const { policiesGettingFullListProgress } = useAppSelector(policiesSelector)
    const profile = useAppSelector(profileSelector)

    const isEhrWriteFlagEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EHR_WRITE_SINGLE,
    )

    const isExportSizeOverLimit = useMemo<boolean>(() => {
        const isEagleEye = !!AuthHelper.getEagleEyeViewAsData()

        return (
            !UtilHelper.isInternalUser(profile) &&
            !isEagleEye &&
            props.currentPaginationTotal >
                COMMON_COVERAGE_HISTORY_CONFIG.maxReportCsvExportChecks
        )
    }, [profile?.email, props.currentPaginationTotal])

    const isExportLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isPoliciesExportLoading()
    }, [runtimeState.isLoading])

    const isEmailTriggerLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isPoliciesExportToEmailLoading()
    }, [runtimeState.isLoading])

    const onExportCsvNow = () => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            policiesExportList({
                practice: selectedPracticeRole.practice,
                payload: {
                    viewType: props.listType,
                    // If EHR write flag is enabled we want to fetch policies from the DB (to get the ehrWriteLog),
                    // otherwise we want to generate them on the fly from the check data to eliminate a query and speed up the process
                    // Also fetch policies from DB in case we are in workflow management list
                    generatePolicyOnTheFly:
                        !isEhrWriteFlagEnabled &&
                        !POLICIES_CONFIG.tasksListTypes.includes(
                            props.listType,
                        ),
                },
            }),
        )
    }

    const onExportCsvEmail = () => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            policiesExportListToEmail({
                practice: selectedPracticeRole.practice,
                payload: {
                    viewType: props.listType,
                },
                onSuccess: () => {
                    ToastrHelper.success(
                        `
                            Your CSV export is running in the background. Feel free to continue with other tasks while we prepare your file.
                            <br/>
                            <br/>
                            Once completed, you'll find your export in email inbox.
                        `,
                        GENERAL_CONFIG.extendedToastrTimeout,
                        true,
                    )
                },
            }),
        )
    }

    return (
        <div
            className={`relative ${props.className} flex flex-col justify-start items-center`}
        >
            <ButtonDropdownElement
                buttonLabel={`Export CSV${
                    isExportLoading
                        ? ` (${policiesGettingFullListProgress}%)`
                        : ""
                }`}
                options={[
                    {
                        label: "Export to email",
                        onClick: onExportCsvEmail,
                    },
                    {
                        label: "Export now",
                        onClick: onExportCsvNow,
                    },
                ]}
                buttonType="default"
                buttonIcon={exportIcon}
                buttonIsLoading={isExportLoading || isEmailTriggerLoading}
                buttonIsDisabled={props.isDisabled || isExportSizeOverLimit}
            />

            <TooltipElement
                className={`mt-[8px] ${isExportSizeOverLimit ? "visible" : "invisible"}`}
                inlineLabel={
                    TOOLTIPS_CONFIG.reports.cantExportResultsText.title
                }
                inlineLabelTypography="captionSemibold"
                isInlined
                text={TOOLTIPS_CONFIG.reports.cantExportResultsText.text}
                backgroundColor={NIRVANA_COLORS.brand.black}
                placement="bottomRight"
            />
        </div>
    )
}

interface IPoliciesExportListComponentProps {
    className?: string
    listType: PoliciesListType
    isDisabled?: boolean
    currentPaginationTotal: number
}
